import { useState } from 'react';
import { Plus, Pencil, Trash2, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API, articleCategories } from './constants';

export default function ArticlesTab({ articles, onRefresh }) {
  const [editingArticle, setEditingArticle] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', category: 'update' });

  const openDialog = (article = null) => {
    if (article) {
      setEditingArticle(article);
      setForm({ title: article.title, content: article.content, category: article.category || 'update' });
    } else {
      setEditingArticle(null);
      setForm({ title: '', content: '', category: 'update' });
    }
    setShowDialog(true);
  };

  const save = async () => {
    if (!form.title || !form.content) {
      toast.error('Please fill in title and content');
      return;
    }
    try {
      if (editingArticle) {
        await axios.put(`${API}/articles/${editingArticle.id}`, form);
        toast.success('Article updated');
      } else {
        await axios.post(`${API}/articles`, form);
        toast.success('Article created');
      }
      setShowDialog(false);
      onRefresh();
    } catch (error) {
      toast.error('Failed to save article');
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this article?')) return;
    try {
      await axios.delete(`${API}/articles/${id}`);
      toast.success('Article deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const togglePublished = async (article) => {
    try {
      await axios.put(`${API}/articles/${article.id}`, { is_published: !article.is_published });
      toast.success(article.is_published ? 'Article unpublished' : 'Article published');
      onRefresh();
    } catch (error) {
      toast.error('Failed to update article');
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Articles ({articles.length})</CardTitle>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button onClick={() => openDialog()} className="gap-2">
              <Plus className="w-4 h-4" /> Add Article
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingArticle ? 'Edit Article' : 'Add Article'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {articleCategories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Content *</Label>
                <Textarea rows={6} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
              </div>
              <Button onClick={save} className="w-full">{editingArticle ? 'Update' : 'Create'} Article</Button>
            </div>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {articles.map((article) => (
            <div key={article.id} className={`p-4 rounded-lg ${article.is_published ? 'bg-muted/50' : 'bg-muted/20 border border-dashed'}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-bold">{article.title}</h4>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{article.content}</p>
                  <span className="text-xs text-muted-foreground">{article.category} • {article.is_published ? 'Published' : 'Draft'}</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => togglePublished(article)}>
                    {article.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => openDialog(article)}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="destructive" size="sm" onClick={() => deleteItem(article.id)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
